import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { NgIcon, provideIcons } from '@ng-icons/core';
import {
  lucideBell,
  lucideChevronRight,
  lucideCreditCard,
  lucideFileText,
  lucideGift,
  lucideHandshake,
  lucideLandmark,
  lucidePalette,
  lucidePercent,
  lucideStore,
  lucideSwatchBook,
  lucideTicket,
  lucideUser,
  lucideUsers,
} from '@ng-icons/lucide';
import { HlmSidebarImports } from '@spartan-ng/helm/sidebar';
import { AuthService } from '../../../services/auth.service';
import {
  SETTINGS_NAV,
  SettingsNavGroup,
  SettingsNavItem,
  isNavItemVisible,
  navLabel,
} from '../settings-nav';

/**
 * Settings navigation. Purely presentational: it filters the nav tree for the
 * current role and reports the selected param, the parent owns the routing.
 *
 * `embedded` drops the card frame and the header, for the mobile menu, which
 * shows these entries under its own collapsible "Einstellungen" entry.
 */
@Component({
  selector: 'app-settings-sidebar',
  imports: [NgTemplateOutlet, NgIcon, HlmSidebarImports],
  providers: [
    provideIcons({
      lucideBell,
      lucideChevronRight,
      lucideCreditCard,
      lucideFileText,
      lucideGift,
      lucideHandshake,
      lucideLandmark,
      lucidePalette,
      lucidePercent,
      lucideStore,
      lucideSwatchBook,
      lucideTicket,
      lucideUser,
      lucideUsers,
    }),
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (embedded()) {
      <ng-container [ngTemplateOutlet]="menu" />
    } @else {
      <!-- The hlm-sidebar wrapper itself is skipped on purpose: with
           collapsible="none" it forces h-svh on its host, which would stretch the
           border across the whole viewport. The menu parts below are unchanged. -->
      <nav
        class="bg-sidebar text-sidebar-foreground border-border flex flex-col overflow-hidden rounded-xl border"
        aria-label="Einstellungen">
        <div hlmSidebarHeader class="px-4 py-3">
          <p class="text-base font-medium">Einstellungen</p>
          <p class="text-muted-foreground text-xs">Konfiguriere deine Gutschein-App</p>
        </div>
        <div hlmSidebarContent>
          <ng-container [ngTemplateOutlet]="menu" />
        </div>
      </nav>
    }

    <ng-template #menu>
      @for (group of visibleGroups(); track group.label; let first = $first) {
        <div hlmSidebarGroup [class.px-0]="embedded()">
          <!-- Embedded, the first group sits right under the "Einstellungen" entry,
               so repeating that label would only add noise. -->
          @if (!(embedded() && first)) {
            <div hlmSidebarGroupLabel>{{ group.label }}</div>
          }
          <div hlmSidebarGroupContent>
            <ul hlmSidebarMenu>
              @for (item of group.items; track item.param) {
                <li hlmSidebarMenuItem>
                  <!-- Hover only tints the background: the text keeps its colour, which
                       otherwise switches to the lighter accent and reads as a thinner font. -->
                  <button
                    hlmSidebarMenuButton
                    class="hover:text-sidebar-foreground active:text-sidebar-foreground data-active:hover:text-sidebar-accent-foreground data-active:active:text-sidebar-accent-foreground"
                    [isActive]="isItemActive(item)"
                    [attr.aria-expanded]="hasSubMenu(item) ? isExpanded(item) : null"
                    (click)="selectItem(item)">
                    <ng-icon [name]="item.icon ?? ''" />
                    <span class="truncate">{{ labelFor(item) }}</span>
                    @if (hasSubMenu(item)) {
                      <!-- Points down while the sub items are open -->
                      <ng-icon
                        name="lucideChevronRight"
                        class="text-muted-foreground ms-auto transition-transform duration-200"
                        [class.rotate-90]="isExpanded(item)" />
                    }
                  </button>

                  <!-- pt-1 instead of py-0.5: the gap to the first sub item matches the
                         gap-1 between the sub items and between the top-level items. -->
                  @if (isExpanded(item) && hasSubMenu(item)) {
                    <ul hlmSidebarMenuSub class="pt-1 pb-0">
                      @for (child of visibleChildren(item); track child.param) {
                        <li hlmSidebarMenuSubItem>
                          <!-- Exactly the top-level style: height, size, and medium weight when active -->
                          <button
                            hlmSidebarMenuSubButton
                            class="data-active:font-medium h-8 text-start text-sm hover:text-sidebar-foreground active:text-sidebar-foreground data-active:hover:text-sidebar-accent-foreground data-active:active:text-sidebar-accent-foreground"
                            [isActive]="activeParam() === child.param"
                            (click)="select.emit(child.param); picked.emit()">
                            <span>{{ labelFor(child) }}</span>
                          </button>
                        </li>
                      }
                    </ul>
                  }
                </li>
              }
            </ul>
          </div>
        </div>
      }
    </ng-template>
  `,
})
export class SettingsSidebarComponent {
  /** Currently selected param, including sub items. */
  readonly activeParam = input<string | null>(null);
  readonly isMember = input(false);
  readonly isNetwork = input(false);
  /** Without frame and header, for the mobile menu. */
  readonly embedded = input(false);

  readonly select = output<string>();
  /**
   * The choice is final: a leaf or a sub item. A parent that just unfolds its
   * sub menu doesn't fire this, so the mobile sheet can stay open for it.
   */
  readonly picked = output<void>();

  /**
   * Embedded only: sub menus the user folded open or shut, by param. Unset
   * entries follow the active page, so the current section starts unfolded.
   */
  private readonly folded = signal<Record<string, boolean>>({});

  private readonly actingPartnerName = inject(AuthService).actingPartnerName;

  private readonly audience = computed(() => ({
    isMember: this.isMember(),
    isNetwork: this.isNetwork(),
    isActingPartner: !!this.actingPartnerName(),
  }));

  protected readonly visibleGroups = computed<SettingsNavGroup[]>(() =>
    SETTINGS_NAV.map((group) => ({
      ...group,
      items: group.items.filter((item) => isNavItemVisible(item, this.audience())),
    })).filter((group) => group.items.length > 0),
  );

  protected visibleChildren(item: SettingsNavItem): SettingsNavItem[] {
    if (!item.children) return [];
    return item.children.filter((child) => isNavItemVisible(child, this.audience()));
  }

  protected labelFor(item: SettingsNavItem): string {
    return navLabel(item, this.audience());
  }

  /**
   * Desktop: a parent opens its first sub item right away. Embedded (mobile) it
   * only unfolds, and the sub item has to be picked, which then closes the menu.
   */
  protected selectItem(item: SettingsNavItem): void {
    if (this.embedded() && this.hasSubMenu(item)) {
      const open = !this.isExpanded(item);
      this.folded.update((state) => ({ ...state, [item.param]: open }));
      return;
    }
    this.select.emit(this.defaultParamFor(item));
    if (!this.hasSubMenu(item)) this.picked.emit();
  }

  /** Whether the sub items show: the active section, or as folded on mobile. */
  protected isExpanded(item: SettingsNavItem): boolean {
    if (!this.embedded()) return this.isItemActive(item);
    return this.folded()[item.param] ?? this.isItemActive(item);
  }

  /** A single visible sub item is the entry itself: no sub list. */
  protected hasSubMenu(item: SettingsNavItem): boolean {
    return this.visibleChildren(item).length > 1;
  }

  /** A parent with children opens its first visible child. */
  protected defaultParamFor(item: SettingsNavItem): string {
    return item.children ? (this.visibleChildren(item)[0]?.param ?? item.param) : item.param;
  }

  protected isItemActive(item: SettingsNavItem): boolean {
    const active = this.activeParam();
    if (!active) return false;
    if (item.param === active) return true;
    return !!item.children?.some((child) => child.param === active);
  }
}
